import { SyncedInput } from "@/components/SyncedInput";
import { useMemo, useState } from "react";
import { Eye, EyeOff, LayoutTemplate, Pencil, Search, ShieldCheck } from "lucide-react";

// Groepering van tabbladen zodat de lijst per rol overzichtelijk blijft.
const GROUPS = [
  { title: "Overzicht & analyse", ids: ["dashboard", "vergelijking", "product", "lijn", "oee", "kostenanalyse", "marges"] },
  { title: "Productie", ids: ["invoer", "planning", "personeel", "ontwikkeling", "behoefte", "halffabricaten", "tracering"] },
  { title: "Voorraad", ids: ["expeditie", "voorraadgrond", "voorraadhulp", "voorraaddiversen", "voorraadvso", "verbruikdiversen", "verbruikvso", "maandafsluiting"] },
  { title: "Beheer", ids: ["recepten", "specificaties", "stam", "kosten", "prijshistorie", "fotos"] },
];

const LEVELS = [
  { key: "none", label: "Uit", short: "Uit", active: "bg-slate-600 text-white", idle: "text-slate-500 hover:bg-slate-100", Icon: EyeOff },
  { key: "view", label: "Alleen lezen", short: "Lezen", active: "bg-amber-500 text-white", idle: "text-amber-700 hover:bg-amber-50", Icon: Eye },
  { key: "edit", label: "Bewerken", short: "Aan", active: "bg-emerald-600 text-white", idle: "text-emerald-700 hover:bg-emerald-50", Icon: Pencil },
];

function Segmented({ value, disabled, onChange, testid }) {
  return (
    <div data-testid={testid} className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {LEVELS.map((lv) => {
        const on = value === lv.key;
        return (
          <button
            key={lv.key}
            type="button"
            data-testid={`${testid}-${lv.key}`}
            disabled={disabled}
            onClick={() => onChange(lv.key)}
            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold transition-colors ${on ? lv.active : lv.idle} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <lv.Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{lv.label}</span>
            <span className="sm:hidden">{lv.short}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function RechtenPaneel({
  roles, roleLabel, saveRoleLabel, roleCanSeeCosts, setRoleCosts,
  roleHasCrm, saveRoleCrm, levelOf, setRoleTab, tabLabelOf, costTabs = [],
  presets = [], onSavePreset, onApplyPreset, onDeletePreset, groups,
  sectionDefs, canSeeSection, setRoleSection,
  mobileTabs, roleMobileTabs, setRoleMobileTab,
}) {
  const [selected, setSelected] = useState(roles[0]);
  const [q, setQ] = useState("");
  const [presetSel, setPresetSel] = useState("");
  const role = roles.includes(selected) ? selected : roles[0];
  const locked = role === "Bedrijfsleiding";
  const query = q.trim().toLowerCase();

  // Groepen komen bij voorkeur mee vanuit de echte sidebar-tabs (altijd up-to-date);
  // valt terug op de ingebouwde lijst als er niets is meegegeven.
  const GROUPS_USED = groups && groups.length ? groups : GROUPS;

  // Tel per rol hoeveel tabs zichtbaar (view/edit) zijn voor het bolletje in de lijst.
  const allTabIds = useMemo(() => GROUPS_USED.flatMap((g) => g.ids), [GROUPS_USED]);
  const counts = useMemo(() => {
    const m = {};
    roles.forEach((r) => {
      let vis = 0;
      allTabIds.forEach((t) => { if (r === "Bedrijfsleiding" || levelOf(r, t) !== "none") vis += 1; });
      m[r] = vis;
    });
    return m;
  }, [roles, allTabIds, levelOf]);

  const setGroup = (g, lvl) => { if (locked) return; g.ids.forEach((t) => setRoleTab(role, t, lvl)); };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_1fr]">
      {/* Rollenlijst */}
      <div className="rounded-xl border border-slate-200 bg-white p-2" data-testid="rechten-rollijst">
        <div className="px-2 py-2 text-xs font-bold uppercase tracking-wide text-slate-400">Rollen</div>
        <div className="space-y-1">
          {roles.map((r) => {
            const on = r === role;
            return (
              <button
                key={r}
                type="button"
                data-testid={`rechten-role-select-${r}`}
                onClick={() => setSelected(r)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition-colors ${on ? "bg-[#1C64B0] text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <span className="flex items-center gap-2 truncate text-sm font-semibold">
                  {r === "Bedrijfsleiding" && <ShieldCheck className={`h-4 w-4 ${on ? "text-white" : "text-[#1C64B0]"}`} />}
                  <span className="truncate">{roleLabel(r)}</span>
                </span>
                <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${on ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                  {r === "Bedrijfsleiding" ? "alles" : `${counts[r]} tabs`}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail geselecteerde rol */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="rechten-detail">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Rolnaam</div>
            <SyncedInput
              key={roleLabel(role)}
              data-testid={`rechten-rolename-${role}`}
              disabled={locked}
              className="mt-1 w-56 rounded-lg border border-slate-300 px-3 py-2 text-lg font-bold text-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
              defaultValue={roleLabel(role)}
              onBlur={(e) => saveRoleLabel(role, e.target.value)}
            />
          </div>
          <div className="flex items-center gap-5">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
              <SyncedInput type="checkbox" data-testid={`rechten-costs-${role}`} className="h-4 w-4 accent-[#1C64B0]" checked={roleCanSeeCosts(role)} disabled={locked} onChange={(e) => setRoleCosts(role, e.target.checked)} />
              Kosten &amp; prijzen
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
              <SyncedInput type="checkbox" data-testid={`rechten-crm-${role}`} className="h-4 w-4 accent-[#1C64B0]" checked={roleHasCrm(role)} disabled={locked} onChange={(e) => saveRoleCrm(role, e.target.checked)} />
              CRM (Sales)
            </label>
          </div>
        </div>

        <div className="mx-5 mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="rechten-preset-bar">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><LayoutTemplate className="h-4 w-4" /> Sjablonen</span>
          <select data-testid="rechten-preset-select" value={presetSel} onChange={(e) => setPresetSel(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
            <option value="">Kies een sjabloon…</option>
            {(presets || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button type="button" data-testid="rechten-preset-apply" disabled={locked || !presetSel} onClick={() => { const p = (presets || []).find((x) => x.id === presetSel); if (p) onApplyPreset(role, p); }} className="rounded-lg bg-[#1C64B0] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#154E8A] disabled:opacity-40">Toepassen op deze rol</button>
          <button type="button" data-testid="rechten-preset-save" onClick={() => { const n = window.prompt("Naam voor dit rechten-sjabloon:", roleLabel(role)); if (n && n.trim()) onSavePreset(role, n.trim()); }} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100">Sla huidige op als sjabloon</button>
          {presetSel && <button type="button" data-testid="rechten-preset-delete" onClick={() => { if (window.confirm("Sjabloon verwijderen?")) { onDeletePreset(presetSel); setPresetSel(""); } }} className="text-xs font-semibold text-rose-600 hover:underline">Verwijderen</button>}
        </div>

        {locked && (
          <div className="mx-5 mt-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Bedrijfsleiding heeft altijd volledige toegang en kan niet worden beperkt.
          </div>
        )}

        <div className="p-5">
          <div className="relative mb-4 max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <SyncedInput data-testid="rechten-zoek" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek tabblad…" className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm" />
          </div>

          <div className="space-y-6">
            {GROUPS_USED.map((g) => {
              const rows = g.ids.filter((t) => !query || (tabLabelOf(t) || t).toLowerCase().includes(query));
              if (!rows.length) return null;
              return (
                <div key={g.title}>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-800">{g.title}</h3>
                    {!locked && (
                      <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                        <span className="mr-1 hidden sm:inline">Alles:</span>
                        <button type="button" data-testid={`rechten-groep-none-${g.title}`} onClick={() => setGroup(g, "none")} className="rounded px-1.5 py-0.5 hover:bg-slate-100 hover:text-slate-700">Uit</button>
                        <button type="button" data-testid={`rechten-groep-view-${g.title}`} onClick={() => setGroup(g, "view")} className="rounded px-1.5 py-0.5 hover:bg-amber-50 hover:text-amber-700">Lezen</button>
                        <button type="button" data-testid={`rechten-groep-edit-${g.title}`} onClick={() => setGroup(g, "edit")} className="rounded px-1.5 py-0.5 hover:bg-emerald-50 hover:text-emerald-700">Bewerken</button>
                      </div>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200">
                    {rows.map((t) => {
                      const isCost = costTabs.includes(t);
                      const noCosts = isCost && !roleCanSeeCosts(role) && !locked;
                      return (
                        <div key={t} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-slate-50">
                          <span className="text-sm font-medium text-slate-700">
                            {tabLabelOf(t) || t}
                            {noCosts && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">verborgen via Kosten</span>}
                          </span>
                          <Segmented
                            testid={`rechten-${role}-${t}`}
                            value={noCosts ? "none" : levelOf(role, t)}
                            disabled={locked || noCosts}
                            onChange={(lvl) => setRoleTab(role, t, lvl)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {sectionDefs && sectionDefs.length > 0 && (
            <div className="mt-8 border-t border-slate-100 pt-6" data-testid="rechten-sections">
              <h3 className="text-sm font-bold text-slate-800 mb-1">Zichtbare onderdelen per scherm</h3>
              <p className="text-xs text-slate-400 mb-3">Zet losse blokken binnen een scherm aan/uit voor deze rol. Hele tabbladen verberg je hierboven.</p>
              <div className="space-y-3">
                {sectionDefs.map((sd) => {
                  const setAll = (v) => { if (locked) return; sd.items.forEach(([id]) => setRoleSection(role, id, v)); };
                  return (
                    <details key={sd.screen} className="overflow-hidden rounded-lg border border-slate-200 bg-white" open>
                      <summary className="flex cursor-pointer select-none items-center justify-between gap-2 bg-slate-50 px-4 py-2.5">
                        <span className="text-sm font-bold text-slate-700">{sd.screen}</span>
                        {!locked && (
                          <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                            <span className="mr-1 hidden sm:inline">Alles:</span>
                            <button type="button" data-testid={`rechten-sec-all-on-${sd.screen}`} onClick={(e) => { e.preventDefault(); setAll(true); }} className="rounded px-1.5 py-0.5 hover:bg-emerald-50 hover:text-emerald-700">Aan</button>
                            <button type="button" data-testid={`rechten-sec-all-off-${sd.screen}`} onClick={(e) => { e.preventDefault(); setAll(false); }} className="rounded px-1.5 py-0.5 hover:bg-slate-100 hover:text-slate-700">Uit</button>
                          </span>
                        )}
                      </summary>
                      <div className="divide-y divide-slate-100">
                        {sd.items.map(([id, label]) => (
                          <label key={id} className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 hover:bg-slate-50">
                            <span className="text-sm font-medium text-slate-700">{label}</span>
                            <SyncedInput type="checkbox" data-testid={`rechten-sec-${role}-${id}`} className="h-4 w-4 accent-[#1C64B0]" checked={canSeeSection(role, id)} disabled={locked} onChange={(e) => setRoleSection(role, id, e.target.checked)} />
                          </label>
                        ))}
                      </div>
                    </details>
                  );
                })}
              </div>
            </div>
          )}
          {mobileTabs && mobileTabs.length > 0 && (
            <div className="mt-8 border-t border-slate-100 pt-6" data-testid="rechten-mobile-tabs">
              <h3 className="text-sm font-bold text-slate-800 mb-1">Mobiele tabbladen (tablet)</h3>
              <p className="text-xs text-slate-400 mb-3">Vink aan welke tabbladen deze rol op de mobiele tablet ziet. <b>Leeg = alle tabbladen zichtbaar.</b> Voor een kantine-/kiosk-tablet vink je bijv. alleen "Klok" aan.</p>
              <div className="flex flex-wrap gap-2">
                {mobileTabs.map(([key, label]) => {
                  const cur = roleMobileTabs ? roleMobileTabs(role) : null;
                  const on = Array.isArray(cur) ? cur.includes(key) : false;
                  return (
                    <label key={key} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${on ? "border-[#1C64B0] bg-blue-50 text-[#1C64B0]" : "border-slate-200 bg-white text-slate-600"} ${locked ? "opacity-50" : ""}`}>
                      <SyncedInput type="checkbox" data-testid={`rechten-mobtab-${role}-${key}`} className="h-4 w-4 accent-[#1C64B0]" checked={on} disabled={locked} onChange={(e) => setRoleMobileTab(role, key, e.target.checked)} />
                      {label}
                    </label>
                  );
                })}
              </div>
              {locked && <p className="text-[11px] text-slate-400 mt-2">Bedrijfsleiding ziet altijd alle tabbladen.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
